import { useEffect } from "react";
import { X, MapPin, Clock, Youtube, ExternalLink, Play } from "lucide-react";
import { ETV_WIN_SERIES_URL, LOGOS } from "../lib/logos";

/**
 * 16:9 YouTube trailer modal — shows the FULL trailer without cropping.
 * Includes a primary "Watch Full Episode on ETV Win" CTA.
 */
export const ReelModal = ({ episode, onClose }) => {
  useEffect(() => {
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  if (!episode) return null;

  const ytId = episode.ytId || extractId(episode.video);
  const embed = `https://www.youtube.com/embed/${ytId}?autoplay=1&modestbranding=1&rel=0&playsinline=1`;
  const watchUrl = episode.youtube || `https://www.youtube.com/watch?v=${ytId}`;

  return (
    <div
      data-testid="reel-modal-backdrop"
      onClick={onClose}
      className="fixed inset-0 z-[800] flex items-center justify-center p-4 md:p-10"
      style={{
        background: "rgba(3,2,1,.9)",
        backdropFilter: "blur(16px)",
        animation: "modalIn .35s ease",
      }}
    >
      <div
        data-testid="reel-modal-content"
        onClick={(e) => e.stopPropagation()}
        className="relative max-w-6xl w-full"
      >
        {/* 16:9 player */}
        <div
          className="relative w-full"
          style={{
            aspectRatio: "16 / 9",
            background: "var(--night)",
            border: "1px solid rgba(201,160,74,.3)",
            overflow: "hidden",
          }}
        >
          <iframe
            data-testid="reel-modal-video"
            title={episode.title}
            src={embed}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            className="absolute inset-0 w-full h-full"
            style={{ border: 0 }}
          />
          <div className="absolute top-3 left-4 st-cinzel text-[0.52rem] tracking-[0.4em] text-[var(--gold-lt)] uppercase z-[2]"
               style={{ textShadow: "0 1px 6px rgba(0,0,0,.8)" }}>
            SOUL TRIP · EP {String(episode.number).padStart(2, "0")}
          </div>
        </div>

        {/* Info row below */}
        <div className="mt-6 grid md:grid-cols-[1.4fr_1fr] gap-6 md:gap-10 text-[var(--cream)]">
          <div>
            <div className="st-eyebrow">{episode.region}</div>
            <h2 className="mt-2 st-cormorant text-2xl md:text-4xl italic font-light leading-tight">
              {episode.title}
            </h2>
            <div className="st-rule mt-3"/>
            <div className="mt-4 st-cormorant italic text-lg text-[var(--gold-lt)]">with {episode.guest}</div>
            <p className="mt-3 st-infant text-[1rem] text-[var(--fog)] leading-relaxed">
              {episode.description}
            </p>
          </div>

          <div className="flex flex-col gap-4 md:items-end justify-start">
            <div className="flex flex-wrap gap-3 st-cinzel text-[0.5rem] tracking-[0.3em] uppercase text-[var(--gold)] md:justify-end">
              <span className="flex items-center gap-2"><MapPin size={11}/> {episode.location}</span>
              <span className="flex items-center gap-2"><Clock size={11}/> {episode.duration}</span>
            </div>

            {/* Primary CTA — Full episode on ETV Win */}
            <a
              data-testid="reel-modal-watch-etvwin"
              href={ETV_WIN_SERIES_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 st-btn st-interactive w-full md:w-auto justify-center"
              style={{
                background: "#e21e26",
                borderColor: "#e21e26",
                color: "#ffffff",
                boxShadow: "0 8px 24px rgba(226,30,38,.25)",
              }}
            >
              <Play size={11} fill="currentColor"/>
              Watch Full Episode on ETV Win <ExternalLink size={11}/>
            </a>

            {/* Secondary — YouTube trailer */}
            <a
              data-testid="reel-modal-watch-yt"
              href={watchUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 st-btn st-interactive"
            >
              <Youtube size={12}/> Watch Trailer on YouTube
            </a>
          </div>
        </div>

        {/* Close */}
        <button
          data-testid="reel-modal-close"
          onClick={onClose}
          aria-label="Close"
          className="absolute -top-4 -right-2 md:-top-4 md:-right-12 w-10 h-10 inline-flex items-center justify-center text-[var(--gold)] st-interactive"
          style={{ background: "rgba(8,6,4,.7)", border: "1px solid rgba(201,160,74,.5)" }}
        >
          <X size={16}/>
        </button>
      </div>

      <style>{`@keyframes modalIn { from { opacity: 0; } to { opacity: 1; } }`}</style>
    </div>
  );
};

function extractId(url) {
  const m = (url || "").match(/youtube\.com\/embed\/([A-Za-z0-9_-]{6,})/);
  return m ? m[1] : "";
}
